import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { movimientosService, cuentasService } from '../services/api';

const MESES = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];

const FORM_PAGO = { fecha: '', monto: '', medio_pago: 'transferencia', cuenta_bancaria_id: '', referencia: '', notas: '' };
const FORM_AJUSTE = { fecha: '', concepto: '', debe: '', haber: '', notas: '' };

const fmtDateISO = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const calcPeriodo = (id, fechaInicioContrato) => {
  const hoy = new Date();
  const y = hoy.getFullYear();
  const m = hoy.getMonth();
  switch (id) {
    case 'este_mes':
      return { desde: fmtDateISO(new Date(y, m, 1)), hasta: fmtDateISO(new Date(y, m + 1, 0)) };
    case 'mes_anterior':
      return { desde: fmtDateISO(new Date(y, m - 1, 1)), hasta: fmtDateISO(new Date(y, m, 0)) };
    case 'este_trimestre': {
      const q = Math.floor(m / 3);
      return { desde: fmtDateISO(new Date(y, q * 3, 1)), hasta: fmtDateISO(new Date(y, q * 3 + 3, 0)) };
    }
    case 'este_anio':
      return { desde: `${y}-01-01`, hasta: fmtDateISO(hoy) };
    case 'desde_inicio': {
      const inicio = fechaInicioContrato ? String(fechaInicioContrato).substring(0, 10) : '2020-01-01';
      return { desde: inicio, hasta: fmtDateISO(hoy) };
    }
    default:
      return null;
  }
};

export default function CuentaCorriente() {
  const { contratoId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [cuentas, setCuentas] = useState([]);
  const [modalPago, setModalPago] = useState(false);
  const [modalAjuste, setModalAjuste] = useState(false);
  const [formPago, setFormPago] = useState(FORM_PAGO);
  const [formAjuste, setFormAjuste] = useState(FORM_AJUSTE);
  const [guardando, setGuardando] = useState(false);
  const [periodoActivo, setPeriodoActivo] = useState('desde_inicio');
  const [desde, setDesde] = useState('2020-01-01');
  const [hasta, setHasta] = useState(fmtDateISO(new Date()));

  const cargar = useCallback(async () => {
    const hoy = new Date();
    await movimientosService.generarCargos({ anio: hoy.getFullYear(), mes: hoy.getMonth() + 1, retroactivo: true });
    const res = await movimientosService.getCuentaCorriente(contratoId);
    setData(res.data);
    // Set "desde inicio" with actual contract start date
    if (res.data.contrato) {
      const p = calcPeriodo('desde_inicio', res.data.contrato.fecha_inicio);
      if (p) { setDesde(p.desde); setHasta(p.hasta); }
    }
  }, [contratoId]);

  useEffect(() => { cargar(); }, [cargar]);
  useEffect(() => { cuentasService.getAll().then(r => setCuentas(r.data)); }, []);

  if (!data) return <div className="empty-state"><p>Cargando...</p></div>;

  const { contrato, movimientos, totales } = data;
  if (!contrato) return <div className="empty-state"><p>Contrato no encontrado</p></div>;

  // Period selector
  const seleccionarPeriodo = (id) => {
    setPeriodoActivo(id);
    if (id !== 'personalizado') {
      const p = calcPeriodo(id, contrato.fecha_inicio);
      if (p) { setDesde(p.desde); setHasta(p.hasta); }
    }
  };

  const navegarMes = (delta) => {
    const d = new Date(desde + 'T00:00:00');
    d.setMonth(d.getMonth() + delta);
    const y = d.getFullYear();
    const m = d.getMonth();
    setDesde(fmtDateISO(new Date(y, m, 1)));
    setHasta(fmtDateISO(new Date(y, m + 1, 0)));
    const hoy = new Date();
    if (y === hoy.getFullYear() && m === hoy.getMonth()) {
      setPeriodoActivo('este_mes');
    } else {
      setPeriodoActivo('personalizado');
    }
  };

  const getPeriodoDisplay = () => {
    const dDesde = new Date(desde + 'T00:00:00');
    const dHasta = new Date(hasta + 'T00:00:00');
    if (dDesde.getMonth() === dHasta.getMonth() && dDesde.getFullYear() === dHasta.getFullYear()) {
      return `${MESES[dDesde.getMonth()]} ${dDesde.getFullYear()}`;
    }
    if (dDesde.getFullYear() === dHasta.getFullYear()) {
      return `${MESES[dDesde.getMonth()]} — ${MESES[dHasta.getMonth()]} ${dHasta.getFullYear()}`;
    }
    return `${MESES[dDesde.getMonth()]} ${dDesde.getFullYear()} — ${MESES[dHasta.getMonth()]} ${dHasta.getFullYear()}`;
  };

  // Filter movements by period
  const movFiltrados = movimientos.filter(m => {
    const fecha = String(m.fecha).substring(0, 10);
    return fecha >= desde && fecha <= hasta;
  });

  // Period totals (filtered)
  const totalDebePeriodo = movFiltrados.reduce((sum, m) => sum + parseFloat(m.debe || 0), 0);
  const totalHaberPeriodo = movFiltrados.reduce((sum, m) => sum + parseFloat(m.haber || 0), 0);

  // Overall saldo (always cumulative)
  const saldo = parseFloat(totales.saldo || 0);

  const fmtMonto = (monto) => {
    const n = parseFloat(monto || 0);
    const prefix = contrato.moneda === 'USD' ? 'US$' : '$';
    return `${prefix} ${n.toLocaleString('es-UY', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  };

  const fmtFecha = (fecha) => {
    if (!fecha) return '-';
    const match = String(fecha).match(/(\d{4})-(\d{2})-(\d{2})/);
    if (!match) return '-';
    return `${match[3]}/${match[2]}/${match[1]}`;
  };

  const cuentasFiltradas = cuentas.filter(c => c.moneda === contrato.moneda);

  // Pago
  const abrirPago = () => {
    setFormPago({ ...FORM_PAGO, fecha: new Date().toISOString().split('T')[0], monto: saldo > 0 ? saldo : '' });
    setModalPago(true);
  };
  const handlePago = async (e) => {
    e.preventDefault();
    setGuardando(true);
    await movimientosService.pagar({ contrato_id: contratoId, ...formPago });
    setModalPago(false);
    setFormPago(FORM_PAGO);
    setGuardando(false);
    cargar();
  };

  // Ajuste
  const abrirAjuste = () => {
    setFormAjuste({ ...FORM_AJUSTE, fecha: new Date().toISOString().split('T')[0] });
    setModalAjuste(true);
  };
  const handleAjuste = async (e) => {
    e.preventDefault();
    setGuardando(true);
    await movimientosService.ajuste({ contrato_id: contratoId, ...formAjuste });
    setModalAjuste(false);
    setFormAjuste(FORM_AJUSTE);
    setGuardando(false);
    cargar();
  };

  const handleEliminar = async (mov) => {
    const tipo = mov.tipo === 'cargo' ? 'cargo' : mov.tipo === 'pago' ? 'pago' : 'ajuste';
    if (window.confirm(`Eliminar este ${tipo}? "${mov.concepto}"`)) {
      try {
        await movimientosService.eliminar(mov.id);
        cargar();
      } catch (err) {
        alert(err.response?.data?.error || 'Error al eliminar');
      }
    }
  };

  return (
    <div>
      {/* Header */}
      <div className="page-header">
        <div>
          <button className="btn btn-secondary" style={{ marginBottom: 8, fontSize: '0.82rem' }} onClick={() => navigate(-1)}>&larr; Volver</button>
          <h1 style={{ marginBottom: 4 }}>{contrato.inmueble_nombre}</h1>
          <p style={{ color: '#666', fontSize: '0.9rem', margin: 0 }}>
            {contrato.inquilino_nombre} {contrato.inquilino_apellido} &middot; {contrato.moneda} {parseFloat(contrato.monto).toLocaleString()}/mes
          </p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button className="btn btn-primary" onClick={abrirPago}>Registrar pago</button>
          <button className="btn btn-secondary" onClick={abrirAjuste}>Ajuste</button>
        </div>
      </div>

      {/* Navegacion de periodo */}
      <div className="mes-nav">
        <button className="btn btn-secondary" onClick={() => navegarMes(-1)}>&larr;</button>
        <h2 className="mes-titulo">{getPeriodoDisplay()}</h2>
        <button className="btn btn-secondary" onClick={() => navegarMes(1)}>&rarr;</button>
        <select value={periodoActivo} onChange={e => seleccionarPeriodo(e.target.value)}
          style={{ marginLeft: 12, padding: '8px 12px', borderRadius: 8, border: '1px solid #ddd', fontSize: '0.88rem' }}>
          <option value="desde_inicio">Desde inicio</option>
          <option value="este_mes">Este mes</option>
          <option value="mes_anterior">Mes anterior</option>
          <option value="este_trimestre">Este trimestre</option>
          <option value="este_anio">Ano actual</option>
          <option value="personalizado">Personalizado</option>
        </select>
        {periodoActivo === 'personalizado' && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginLeft: 12 }}>
            <label style={{ fontSize: '0.85rem', color: '#666' }}>Desde</label>
            <input type="date" value={desde} onChange={e => { setDesde(e.target.value); setPeriodoActivo('personalizado'); }} className="mes-input" />
            <label style={{ fontSize: '0.85rem', color: '#666' }}>Hasta</label>
            <input type="date" value={hasta} onChange={e => { setHasta(e.target.value); setPeriodoActivo('personalizado'); }} className="mes-input" />
          </div>
        )}
      </div>

      {/* Stats */}
      <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
        <div className="stat-card" style={{ borderLeft: '4px solid #e74c3c' }}>
          <div className="label">Cargos del periodo</div>
          <div className="number">{fmtMonto(totalDebePeriodo)}</div>
        </div>
        <div className="stat-card" style={{ borderLeft: '4px solid #28a745' }}>
          <div className="label">Pagado del periodo</div>
          <div className="number" style={{ color: '#28a745' }}>{fmtMonto(totalHaberPeriodo)}</div>
        </div>
        <div className="stat-card" style={{ borderLeft: `4px solid ${saldo > 0 ? '#e74c3c' : '#28a745'}` }}>
          <div className="label">Saldo total</div>
          <div className="number" style={{ color: saldo > 0 ? '#e74c3c' : '#28a745' }}>
            {saldo > 0 ? fmtMonto(saldo) : saldo === 0 ? 'Al dia' : `${fmtMonto(Math.abs(saldo))} a favor`}
          </div>
        </div>
      </div>

      {/* Tabla estado de cuenta */}
      <div className="table-container" style={{ marginTop: 20 }}>
        <table>
          <thead>
            <tr>
              <th>Fecha</th>
              <th>Concepto</th>
              <th style={{ textAlign: 'right' }}>Debe</th>
              <th style={{ textAlign: 'right' }}>Haber</th>
              <th style={{ textAlign: 'right' }}>Saldo</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {movFiltrados.length === 0 ? (
              <tr><td colSpan={6} style={{ textAlign: 'center', color: '#bbb', padding: 20 }}>Sin movimientos en este periodo</td></tr>
            ) : movFiltrados.map(m => {
              const saldoAcum = parseFloat(m.saldo_acumulado);
              return (
                <tr key={m.id} style={{
                  background: m.tipo === 'pago' ? '#f0fff4' : m.tipo === 'ajuste' ? '#fff8e1' : 'white'
                }}>
                  <td style={{ fontSize: '0.85rem', whiteSpace: 'nowrap' }}>{fmtFecha(m.fecha)}</td>
                  <td>
                    <div style={{ fontWeight: m.tipo === 'cargo' ? 600 : 400, fontSize: '0.88rem' }}>{m.concepto}</div>
                    {m.medio_pago && (
                      <div style={{ fontSize: '0.75rem', color: '#888' }}>
                        {m.medio_pago}{m.cuenta_nombre ? ` · ${m.cuenta_nombre}` : ''}{m.referencia ? ` · Ref: ${m.referencia}` : ''}
                      </div>
                    )}
                  </td>
                  <td style={{ textAlign: 'right', color: '#e74c3c', fontWeight: 600, fontSize: '0.9rem' }}>
                    {parseFloat(m.debe) > 0 ? fmtMonto(m.debe) : ''}
                  </td>
                  <td style={{ textAlign: 'right', color: '#28a745', fontWeight: 600, fontSize: '0.9rem' }}>
                    {parseFloat(m.haber) > 0 ? fmtMonto(m.haber) : ''}
                  </td>
                  <td style={{
                    textAlign: 'right', fontWeight: 700, fontSize: '0.9rem',
                    color: saldoAcum > 0 ? '#e74c3c' : saldoAcum === 0 ? '#28a745' : '#4f8ef7'
                  }}>
                    {fmtMonto(Math.abs(saldoAcum))}{saldoAcum < 0 ? ' (a favor)' : ''}
                  </td>
                  <td>
                    <button onClick={() => handleEliminar(m)} title="Eliminar"
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ccc', fontSize: '0.9rem', padding: 4 }}
                      onMouseOver={e => e.target.style.color = '#e74c3c'}
                      onMouseOut={e => e.target.style.color = '#ccc'}>
                      🗑
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
          {movFiltrados.length > 0 && (
            <tfoot>
              <tr style={{ fontWeight: 700, borderTop: '2px solid #1a1a2e' }}>
                <td colSpan={2} style={{ textAlign: 'right' }}>Totales del periodo</td>
                <td style={{ textAlign: 'right', color: '#e74c3c' }}>{fmtMonto(totalDebePeriodo)}</td>
                <td style={{ textAlign: 'right', color: '#28a745' }}>{fmtMonto(totalHaberPeriodo)}</td>
                <td style={{ textAlign: 'right', color: saldo > 0 ? '#e74c3c' : '#28a745' }}>
                  {fmtMonto(Math.abs(saldo))}
                </td>
                <td></td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {/* Modal Pago */}
      {modalPago && (
        <div className="modal-overlay" onClick={() => setModalPago(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h2>Registrar pago</h2>
            <p style={{ color: '#666', marginBottom: 16, fontSize: '0.9rem' }}>
              <strong>{contrato.inmueble_nombre}</strong> &middot; {contrato.inquilino_nombre} {contrato.inquilino_apellido}
              <br />Saldo adeudado: <strong style={{ color: '#e74c3c' }}>{fmtMonto(saldo)}</strong>
            </p>
            <form onSubmit={handlePago}>
              <div className="form-grid">
                <div className="form-group">
                  <label>Fecha de pago *</label>
                  <input type="date" value={formPago.fecha} onChange={e => setFormPago(f => ({ ...f, fecha: e.target.value }))} required />
                </div>
                <div className="form-group">
                  <label>Monto *</label>
                  <input type="number" step="0.01" value={formPago.monto} onChange={e => setFormPago(f => ({ ...f, monto: e.target.value }))} required />
                </div>
                <div className="form-group">
                  <label>Medio de pago</label>
                  <select value={formPago.medio_pago} onChange={e => setFormPago(f => ({ ...f, medio_pago: e.target.value }))}>
                    <option value="transferencia">Transferencia</option>
                    <option value="efectivo">Efectivo</option>
                    <option value="cheque">Cheque</option>
                  </select>
                </div>
                {formPago.medio_pago === 'transferencia' && (
                  <div className="form-group">
                    <label>Cuenta bancaria</label>
                    <select value={formPago.cuenta_bancaria_id} onChange={e => setFormPago(f => ({ ...f, cuenta_bancaria_id: e.target.value }))}>
                      <option value="">Seleccionar...</option>
                      {cuentasFiltradas.map(c => <option key={c.id} value={c.id}>{c.nombre}</option>)}
                    </select>
                  </div>
                )}
                <div className="form-group">
                  <label>Referencia</label>
                  <input value={formPago.referencia} onChange={e => setFormPago(f => ({ ...f, referencia: e.target.value }))} placeholder="Nro. transferencia, cheque, etc." />
                </div>
                <div className="form-group full">
                  <label>Notas</label>
                  <textarea value={formPago.notas} onChange={e => setFormPago(f => ({ ...f, notas: e.target.value }))} rows={2} />
                </div>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setModalPago(false)}>Cancelar</button>
                <button type="submit" className="btn btn-primary" disabled={guardando}>
                  {guardando ? 'Guardando...' : 'Registrar pago'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Ajuste */}
      {modalAjuste && (
        <div className="modal-overlay" onClick={() => setModalAjuste(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h2>Agregar ajuste</h2>
            <form onSubmit={handleAjuste}>
              <div className="form-grid">
                <div className="form-group">
                  <label>Fecha *</label>
                  <input type="date" value={formAjuste.fecha} onChange={e => setFormAjuste(f => ({ ...f, fecha: e.target.value }))} required />
                </div>
                <div className="form-group full">
                  <label>Concepto *</label>
                  <input value={formAjuste.concepto} onChange={e => setFormAjuste(f => ({ ...f, concepto: e.target.value }))} placeholder="Ej: Recargo por mora, Nota de credito, etc." required />
                </div>
                <div className="form-group">
                  <label>Debe (cargo al inquilino)</label>
                  <input type="number" step="0.01" value={formAjuste.debe} onChange={e => setFormAjuste(f => ({ ...f, debe: e.target.value }))} placeholder="0" />
                </div>
                <div className="form-group">
                  <label>Haber (credito al inquilino)</label>
                  <input type="number" step="0.01" value={formAjuste.haber} onChange={e => setFormAjuste(f => ({ ...f, haber: e.target.value }))} placeholder="0" />
                </div>
                <div className="form-group full">
                  <label>Notas</label>
                  <textarea value={formAjuste.notas} onChange={e => setFormAjuste(f => ({ ...f, notas: e.target.value }))} rows={2} />
                </div>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setModalAjuste(false)}>Cancelar</button>
                <button type="submit" className="btn btn-primary" disabled={guardando}>
                  {guardando ? 'Guardando...' : 'Guardar ajuste'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
